import React, { createContext, useLayoutEffect, useMemo } from 'react'

import { useAuth } from '#hooks/useAuth'

import { lookForRoles, readStoredLook, storeLook } from './helpers'
import type { ILookContext, IProps, TLook } from './interfaces'

export const LookContext = createContext<ILookContext>({ look: 'classic' })

/**
 * Sets `data-look` on <html> (FE-013). The look follows the signed-in user's
 * highest role; without a user (sign-in, public pages) it keeps the last
 * stored look. While impersonating, the impersonated user's look shows but is
 * not stored: impersonation does not survive a reload, so the next paint
 * belongs to the real user again.
 */
export const LookProvider = ({ children, override }: IProps): React.JSX.Element => {
  const { user, isImpersonating } = useAuth()

  const look = useMemo((): TLook => {
    if (override) return override
    if (user) return lookForRoles(user.roles)
    return readStoredLook()
  }, [override, user])

  useLayoutEffect(() => {
    document.documentElement.setAttribute('data-look', look)
    if (!override && user && !isImpersonating) storeLook(look)
  }, [look, override, user, isImpersonating])

  const value = useMemo((): ILookContext => ({ look }), [look])

  return <LookContext value={value}>{children}</LookContext>
}
